import { useCallback, useId, useMemo, useRef, useState } from 'react'
import { Plus, X } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

export type TokenSuggestion = {
  name: string
  /** Optional secondary label in the suggestion list. */
  hint?: string
}

export type TokenChipFieldProps = {
  id?: string
  tokens: string[]
  onAdd: (token: string) => void
  onRemove: (token: string) => void
  /** Optional filtered suggestions; when omitted, only freeform create is available. */
  suggestions?: TokenSuggestion[]
  suggestionsLoading?: boolean
  disabled?: boolean
  placeholder?: string
  helperText?: string
  allowCreate?: boolean
  createLabel?: (query: string) => string
  emptySuggestionsLabel?: string
  className?: string
}

/**
 * Generic chip/token editor: chips inside a field, type to filter suggestions,
 * optional create row for unmatched names. Domain wrappers (e.g. SkillChipField)
 * supply catalog suggestions.
 */
export function TokenChipField({
  id: idProp,
  tokens,
  onAdd,
  onRemove,
  suggestions = [],
  suggestionsLoading = false,
  disabled,
  placeholder = 'Add…',
  helperText,
  allowCreate = true,
  createLabel = (q) => `Create "${q}"`,
  emptySuggestionsLabel = 'Start typing to search, or type a new value.',
  className,
}: TokenChipFieldProps) {
  const generatedId = useId()
  const id = idProp ?? generatedId
  const panelId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [panelOpen, setPanelOpen] = useState(false)
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearBlurTimer = useCallback(() => {
    if (blurTimer.current) {
      clearTimeout(blurTimer.current)
      blurTimer.current = null
    }
  }, [])

  const closePanelSoon = useCallback(() => {
    clearBlurTimer()
    blurTimer.current = setTimeout(() => setPanelOpen(false), 120)
  }, [clearBlurTimer])

  const selectedLower = useMemo(
    () => new Set(tokens.map((s) => s.trim().toLowerCase()).filter(Boolean)),
    [tokens],
  )

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return suggestions
      .filter((item) => {
        const key = item.name.trim().toLowerCase()
        if (!key || selectedLower.has(key)) return false
        if (!q) return true
        return item.name.toLowerCase().includes(q)
      })
      .slice(0, q ? 80 : 60)
  }, [suggestions, selectedLower, query])

  const trimmedQuery = query.trim()
  const queryLower = trimmedQuery.toLowerCase()
  const showCreateRow =
    allowCreate &&
    trimmedQuery.length > 0 &&
    !selectedLower.has(queryLower) &&
    !filtered.some((item) => item.name.trim().toLowerCase() === queryLower)

  const addAndReset = useCallback(
    (name: string) => {
      clearBlurTimer()
      const cleaned = name.trim()
      if (!cleaned) return
      onAdd(cleaned)
      setQuery('')
      inputRef.current?.focus()
    },
    [clearBlurTimer, onAdd],
  )

  const resolveAndAdd = useCallback(() => {
    if (!trimmedQuery) return
    if (selectedLower.has(queryLower)) {
      setQuery('')
      return
    }
    const match = filtered.find((item) => item.name.trim().toLowerCase() === queryLower)
    addAndReset(match?.name ?? trimmedQuery)
  }, [addAndReset, filtered, queryLower, selectedLower, trimmedQuery])

  const showPanel =
    panelOpen &&
    !disabled &&
    (suggestionsLoading || filtered.length > 0 || trimmedQuery.length > 0 || suggestions.length > 0)

  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="relative">
        <div
          className={cn(
            'flex min-h-9 w-full flex-wrap items-center gap-1.5 rounded-md border border-input bg-transparent px-2.5 py-1.5 text-sm shadow-sm transition-colors',
            'focus-within:outline-none focus-within:ring-1 focus-within:ring-ring',
            disabled && 'cursor-not-allowed opacity-50',
          )}
          onClick={() => inputRef.current?.focus()}
        >
          {tokens.map((token) => (
            <Badge
              key={token}
              variant="secondary"
              className="flex items-center gap-1 px-2 py-0.5 font-normal"
            >
              {token}
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation()
                  onRemove(token)
                }}
                disabled={disabled}
                aria-label={`Remove ${token}`}
                title={`Remove ${token}`}
                className="rounded-sm text-muted-foreground transition-colors hover:text-destructive focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
              >
                <X className="size-3" />
              </button>
            </Badge>
          ))}
          <input
            ref={inputRef}
            id={id}
            role="combobox"
            aria-expanded={showPanel}
            aria-controls={showPanel ? panelId : undefined}
            aria-autocomplete="list"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value)
              setPanelOpen(true)
            }}
            onFocus={() => setPanelOpen(true)}
            onBlur={closePanelSoon}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                resolveAndAdd()
              }
              if (event.key === 'Escape') {
                setPanelOpen(false)
              }
              if (event.key === 'Backspace' && query.length === 0 && tokens.length > 0) {
                event.preventDefault()
                onRemove(tokens[tokens.length - 1])
              }
            }}
            placeholder={tokens.length === 0 ? placeholder : 'Add another…'}
            disabled={disabled}
            autoComplete="off"
            className="h-6 min-w-[8rem] flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
          />
        </div>
        {showPanel ? (
          <ul
            id={panelId}
            role="listbox"
            className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-md border border-border bg-popover py-1 text-sm shadow-md"
          >
            {suggestionsLoading ? (
              <li className="px-3 py-2 text-muted-foreground">Loading…</li>
            ) : null}
            {filtered.map((item) => (
              <li key={item.name}>
                <button
                  type="button"
                  role="option"
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-accent"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => addAndReset(item.name)}
                >
                  <span className="truncate">{item.name}</span>
                  {item.hint ? (
                    <span className="ml-auto truncate text-xs text-muted-foreground">
                      {item.hint}
                    </span>
                  ) : null}
                </button>
              </li>
            ))}
            {showCreateRow ? (
              <li>
                <button
                  type="button"
                  role="option"
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-accent"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => addAndReset(trimmedQuery)}
                >
                  <Plus className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                  <span className="truncate">{createLabel(trimmedQuery)}</span>
                </button>
              </li>
            ) : null}
            {!suggestionsLoading &&
            filtered.length === 0 &&
            trimmedQuery.length === 0 &&
            !showCreateRow ? (
              <li className="px-3 py-2 text-muted-foreground">{emptySuggestionsLabel}</li>
            ) : null}
          </ul>
        ) : null}
      </div>
      {helperText ? <p className="text-xs text-muted-foreground">{helperText}</p> : null}
    </div>
  )
}
